import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { UseCashierHomeLayoutReturn } from "../hooks/types.ts";
import DiscountPanel from "./DiscountPanel.tsx";
import OrderItemsList from "./OrderItemsList.tsx";
import OrderSummary from "./OrderSummary.tsx";
import SplitByAmount from "./SplitByAmount.tsx";
import SplitByItems from "./SplitByItems.tsx";
import CheckoutSegmentedTabs from "../ui/CheckoutSegmentedTabs.tsx";
import CustomItemModal from "./CustomItemModal.tsx";
import "../custom-item-ui.css";

interface CheckoutSidebarProps {
  state: UseCashierHomeLayoutReturn;
}

const CheckoutSidebarComponent: React.FC<CheckoutSidebarProps> = ({ state }) => {
  const { t } = useTranslation();
  const { ui, order, checkout, mutations, discounts, splitPayment } = state;
  const [isCustomItemOpen, setIsCustomItemOpen] = useState(false);
  const suppressPaymentRef = useRef(false);

  const handlePay = () => {
    if (isCustomItemOpen || suppressPaymentRef.current) return;
    checkout.handleSubmit();
  };

  const handleCustomItemSubmit = async (draft: Parameters<NonNullable<typeof mutations.handleAddCustomItem>>[0]) => {
    suppressPaymentRef.current = true;
    const saved = await mutations.handleAddCustomItem?.(draft);
    if (saved) setIsCustomItemOpen(false);
    window.setTimeout(() => {
      suppressPaymentRef.current = false;
    }, 0);
  };

  return (
    <>
      <div
        className="dashboard-cashier-right-in cashier-checkout-form split-ui-layout"
      >
        <div className="buttom-bill-info">
          <CheckoutSegmentedTabs
            className="checkout-sidebar-tabs"
            buttonClassName="checkout-sidebar-tab"
            value={ui.activeSidebarTab}
            onChange={ui.setActiveSidebarTab}
            options={[
              { value: "items", label: t("heading_order_items") },
              { value: "payment", label: t("payment") },
            ]}
          />

          <div className="checkout-scroll-area">
            {ui.activeSidebarTab === "items" ? (
              <div className="checkout-order-items-shell">
                <div
                  className="checkout-tab-panel checkout-order-items-panel"
                  ref={ui.orderItemsPanelRef}
                  onScroll={ui.updateOrderItemsScrollIndicator}
                >
                  <OrderItemsList
                    items={order.orderItems}
                    paidItems={[
                      ...(order.tableDetail.activeOrder?.paid_items || []),
                      ...(order.tableDetail.activeOrder?.subOrders || []).flatMap(
                        (subOrder) => subOrder?.paid_items || [],
                      ),
                    ]}
                    showPaymentStatus={
                      splitPayment.isSplitCheckout && splitPayment.lockedSplitMode === "items"
                    }
                    updatingKey={mutations.updatingOrderItemKey}
                    onDecreaseQuantity={mutations.handleDecreaseQuantity}
                    onIncreaseQuantity={mutations.handleIncreaseQuantity}
                    onDeleteItem={mutations.handleInlineDeleteItem}
                    onSetQuantity={mutations.handleSetQuantity}
                  />
                </div>

                {ui.showOrderItemsScrollIndicator ? (
                  <button
                    type="button"
                    className="split-scroll-indicator"
                    onClick={ui.handleOrderItemsScrollIndicatorClick}
                    aria-label={t("heading_order_items")}
                  >
                    <i className="ti ti-chevron-down" />
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="checkout-payment-panel">
                <div className="checkout-mode-panel">
                  <CheckoutSegmentedTabs
                    className="checkout-mode-tabs"
                    buttonClassName="checkout-mode-tab"
                    value={splitPayment.isSplitCheckout ? "split" : "all_together"}
                    onChange={splitPayment.handleCheckoutModeChange}
                    options={[
                      {
                        value: "all_together",
                        label: t("label_pay_together"),
                        disabled: splitPayment.hasSplitPayment,
                      },
                      {
                        value: "split",
                        label: t("label_split_pay"),
                      },
                    ]}
                  />
                </div>

                {splitPayment.isSplitCheckout ? (
                  <CheckoutSegmentedTabs
                    className="split-mode-tabs compact-split-tabs"
                    buttonClassName="split-mode-tab"
                    value={splitPayment.lockedSplitMode}
                    onChange={splitPayment.handleSplitModeChange}
                    options={[
                      {
                        value: "items",
                        label: t("label_by_items"),
                        disabled: splitPayment.isItemsModeDisabled,
                      },
                      {
                        value: "amount",
                        label: t("label_by_amount"),
                        disabled: splitPayment.isAmountModeDisabled,
                      },
                    ]}
                  />
                ) : null}

                {splitPayment.isSplitCheckout ? (
                  <div className="split-sidebar-body">
                    {splitPayment.lockedSplitMode === "amount" ? (
                      <SplitByAmount splitPayment={splitPayment} />
                    ) : (
                      <SplitByItems splitPayment={splitPayment} />
                    )}
                  </div>
                ) : (
                  <DiscountPanel discounts={discounts} tableDetail={order.tableDetail} />
                )}
              </div>
            )}
          </div>

          <OrderSummary
            order={order}
            ui={ui}
            splitPayment={splitPayment}
            onSendToKitchen={mutations.handleSendToKitchen}
            onPrintTableSlip={mutations.handlePrintTableSlip}
            isPrintingTableSlip={mutations.isPrintingTableSlip}
            onPay={handlePay}
            onAddCustomItem={() => setIsCustomItemOpen(true)}
            canAddCustomItem={!splitPayment.hasSplitPayment}
          />
        </div>
      </div>

      <CustomItemModal
        open={isCustomItemOpen}
        onClose={() => setIsCustomItemOpen(false)}
        onSubmit={handleCustomItemSubmit}
        isSaving={mutations.isAddingCustomItem}
      />
    </>
  );
};

const CheckoutSidebar = React.memo(CheckoutSidebarComponent);

export default CheckoutSidebar;
